import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, type JSX } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { apiErrorMessage } from "@/api/errors";
import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import { toast } from "@/shared/lib/toast";

import { useBundle } from "../api/use-bundle";
import { useUpdateBundle } from "../api/use-update-bundle";
import { componentRowSchema } from "../lib/create-sku-bundle-schema";

import { BaseProductPicker } from "./BaseProductPicker";

interface BundleFormModalProps {
  /** The bundle code being edited. The unified Create modal owns creation. */
  code: string;
  onClose: () => void;
}

const editBundleSchema = z.object({
  product_name: z.string().min(1, "Required").max(255, "Max 255 characters"),
  components: z.array(componentRowSchema).min(1, "At least one component is required"),
});

type EditBundleFormInput = z.input<typeof editBundleSchema>;
type EditBundleFormValues = z.infer<typeof editBundleSchema>;

const FIELD_CLASS =
  "h-10 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";

export function BundleFormModal({ code, onClose }: BundleFormModalProps): JSX.Element {
  const { t } = useTranslation("inventory");

  const detail = useBundle(code);
  const update = useUpdateBundle();

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<EditBundleFormInput, unknown, EditBundleFormValues>({
    resolver: zodResolver(editBundleSchema),
    defaultValues: { product_name: "", components: [] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "components" });
  const pickedSkus = fields.map((f) => f.base_sino_sku);

  // Hydrate the form once the detail loads.
  const detailData = detail.data;
  useEffect(() => {
    if (!detailData) return;
    reset({
      product_name: detailData.product_name ?? "",
      components: (detailData.components ?? []).map((c) => ({
        base_sino_sku: c.base_sino_sku,
        multiplicity: c.multiplicity,
      })),
    });
  }, [detailData, reset]);

  const isPending = update.isPending;
  const serverError = update.error;
  const sellable = detailData?.sellable ?? [];

  function onSubmit(values: EditBundleFormValues): void {
    update.mutate(
      {
        code,
        body: {
          product_name: values.product_name,
          components: values.components.map((c) => ({
            base_sino_sku: c.base_sino_sku,
            multiplicity: c.multiplicity,
          })),
        },
      },
      {
        onSuccess: () => {
          toast.success(t("bundle.toast.updated", { code }));
          onClose();
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("bundle.modal.edit.title")}
      data-testid="bundle-form-modal"
    >
      <button
        type="button"
        aria-label={t("bundle.modal.action_cancel")}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{ background: "rgba(16,24,40,.28)", backdropFilter: "blur(2px)" }}
      />
      <div
        className="relative max-h-[88vh] w-full max-w-[560px] overflow-y-auto rounded-2xl bg-white"
        style={{ boxShadow: "var(--sh-modal)" }}
      >
        <h2 className="px-6 pt-5 text-[17px] font-extrabold tracking-tight text-[var(--text-primary)]">
          {t("bundle.modal.edit.title")}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 px-6 pb-6 pt-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]">
              {t("bundle.modal.field_code")}
            </label>
            <input
              type="text"
              data-testid="bundle-code"
              disabled
              value={code}
              readOnly
              className={`sku ${FIELD_CLASS}`}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]">
              {t("bundle.modal.field_name")}
            </label>
            <input
              type="text"
              data-testid="bundle-product-name"
              disabled={isPending}
              placeholder={t("bundle.modal.placeholder_name")}
              className={FIELD_CLASS}
              {...register("product_name")}
            />
            {errors.product_name && (
              <p
                className="mt-1 text-xs text-[var(--intent-danger-fg)]"
                data-testid="bundle-product-name-error"
              >
                {errors.product_name.message}
              </p>
            )}
          </div>

          <fieldset className="space-y-2.5">
            <legend className="mb-0.5 text-xs font-semibold text-[var(--text-secondary)]">
              {t("bundle.modal.components_legend")}
            </legend>

            <BaseProductPicker
              disabled={isPending}
              excludeSkus={pickedSkus}
              onSelect={(item) =>
                append({
                  base_sino_sku: item.sku,
                  product_name: item.product_name,
                  multiplicity: 1,
                })
              }
            />

            <div className="space-y-2" data-testid="bundle-components">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-center gap-2 rounded-[var(--control-r)] border border-[var(--premium-border)] px-3 py-2"
                  data-testid="bundle-component-row"
                >
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-sm font-semibold text-[var(--text-primary)]"
                      data-testid={`bundle-component-sku-${index}`}
                    >
                      {field.product_name || field.base_sino_sku}
                    </p>
                    <p className="sku text-xs text-[var(--text-subtle)]">{field.base_sino_sku}</p>
                  </div>
                  <Controller
                    control={control}
                    name={`components.${index}.multiplicity`}
                    render={({ field: ctrl }) => (
                      <input
                        type="number"
                        min={1}
                        step={1}
                        data-testid={`bundle-component-multiplicity-${index}`}
                        disabled={isPending}
                        aria-label={t("bundle.modal.placeholder_multiplicity")}
                        className={`${FIELD_CLASS.replace("w-full ", "")} w-20 text-right`}
                        value={ctrl.value == null ? "" : String(ctrl.value)}
                        onChange={(e) => ctrl.onChange(e.target.value)}
                        onBlur={ctrl.onBlur}
                      />
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    disabled={isPending}
                    aria-label={t("bundle.modal.action_remove_component")}
                    data-testid={`bundle-component-remove-${index}`}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] text-[var(--text-subtle)] transition hover:bg-[var(--canvas)] disabled:opacity-40"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              ))}
            </div>

            {fields.map((field, index) =>
              errors.components?.[index]?.multiplicity ? (
                <p
                  key={`err-${field.id}`}
                  className="text-xs text-[var(--intent-danger-fg)]"
                  data-testid={`bundle-component-multiplicity-error-${index}`}
                >
                  {errors.components[index]?.multiplicity?.message}
                </p>
              ) : null,
            )}

            {typeof errors.components?.message === "string" && (
              <p
                className="mt-1 text-xs text-[var(--intent-danger-fg)]"
                data-testid="bundle-components-error"
              >
                {errors.components.message}
              </p>
            )}
          </fieldset>

          <div data-testid="bundle-sellable">
            <p className="mb-1.5 text-xs font-semibold text-[var(--text-secondary)]">
              {t("bundle.modal.sellable_legend")}
            </p>
            {detail.isLoading ? (
              <p className="text-[12.5px] text-[var(--text-subtle)]">
                {t("bundle.modal.sellable_loading")}
              </p>
            ) : sellable.length === 0 ? (
              <p className="text-[12.5px] text-[var(--text-subtle)]">
                {t("bundle.modal.sellable_empty")}
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {sellable.map((s) => (
                  <div
                    key={s.marketplace}
                    data-testid={`bundle-sellable-${s.marketplace}`}
                    className="flex items-center justify-between rounded-[var(--control-r)] border border-[var(--premium-border)] px-3 py-2"
                  >
                    {s.marketplace ? (
                      <MarketplaceChip marketplace={s.marketplace} />
                    ) : (
                      <span className="text-sm text-[var(--text-subtle)]">{s.marketplace}</span>
                    )}
                    <span className="num text-sm font-bold tabular-nums text-[var(--text-primary)]">
                      {(s.sellable ?? 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {serverError && (
            <p className="text-sm text-[var(--intent-danger-fg)]" data-testid="bundle-server-error">
              {apiErrorMessage(serverError)}
            </p>
          )}

          <div className="flex justify-end gap-2.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              data-testid="bundle-cancel"
              className="h-10 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] px-4 text-[13.5px] font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--canvas)] disabled:opacity-50"
            >
              {t("bundle.modal.action_cancel")}
            </button>
            <button
              type="submit"
              disabled={isPending}
              data-testid="bundle-submit"
              className="inline-flex h-10 items-center gap-1.5 rounded-[var(--control-r)] px-5 text-[13.5px] font-bold text-white transition hover:-translate-y-px disabled:opacity-50"
              style={{
                background: "var(--grad-primary-btn)",
                boxShadow:
                  "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5),inset 0 1px 0 rgba(255,255,255,.22)",
              }}
            >
              {isPending ? t("bundle.modal.action_saving") : t("bundle.modal.action_save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
